import * as React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import { toast } from "@/admin/hooks/use-toast";
import { PlanType, useCreateAdminCampaign } from "@/admin/services";

import { PageHeader } from "../../_components/page-header";
import {
  CampaignForm,
  CampaignFormState,
  initialCampaignFormState,
} from "../_components/campaign-form";

export const AdminCreateCampaignPage = () => {
  const [form, setForm] = React.useState<CampaignFormState>(initialCampaignFormState);
  const create = useCreateAdminCampaign();
  const navigate = useNavigate();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name || !form.slug) {
      toast({ variant: "error", title: "Name and slug are required." });
      return;
    }
    if (new Date(form.endsAt) <= new Date(form.startsAt)) {
      toast({ variant: "error", title: "End date must be after start date." });
      return;
    }

    try {
      const payload = {
        slug: form.slug,
        name: form.name,
        description: form.description || form.name,
        offerType: form.offerType,
        grantedPlan: form.grantedPlan,
        grantedDurationDays: form.offerType === "STRIPE_DISCOUNT" ? 0 : form.grantedDurationDays,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
        targetAudience: form.targetAudience,
        maxRedemptions: form.maxRedemptions || null,
        bannerText: form.bannerText || null,
        pageSlug: form.slug,
        pageHeadline: form.pageHeadline || null,
        pageSubheadline: form.pageSubheadline || null,
        discountPercent: form.offerType === "STRIPE_DISCOUNT" ? form.discountPercent : null,
        discountPlans:
          form.offerType === "STRIPE_DISCOUNT" ? (["pro"] as PlanType[]) : ([] as PlanType[]),
        discountDurationMonths:
          form.offerType === "STRIPE_DISCOUNT" ? form.discountDurationMonths : null,
        stripeCouponId:
          form.offerType === "STRIPE_DISCOUNT" && form.enableStripeCouponOverride
            ? form.stripeCouponId
            : null,
      };
      const created = await create.mutateAsync(payload);
      toast({ title: `Created “${created.name}”.` });
      navigate(`/campaigns/${created.id}`);
    } catch {
      // Axios interceptor already toasts the server message.
    }
  };

  return (
    <>
      <Helmet>
        <title>New campaign · Admin</title>
      </Helmet>

      <PageHeader title="New campaign" trail={["Admin", "Campaigns"]} />

      <CampaignForm
        value={form}
        onChange={setForm}
        onSubmit={submit}
        onCancel={() => navigate("/campaigns")}
        mode="create"
        submitLabel="Save campaign"
        isSubmitting={create.isPending}
      />
    </>
  );
};

export default AdminCreateCampaignPage;
